/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type { Theme } from '@mui/material';
import type { SxProps } from '@mui/system';
import { type DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { formatDate, prettifySnakeCase } from '@poweralpha/common';
import { useCallback, useMemo } from 'react';
import { type DateEntryMode, getDate } from '../../utils';
import InputKeyValue from '../display';
import { FormatValue } from '../FormatValue';
import { useFieldContext } from './formContext';

const tinyTextFieldSx = {
  '& .MuiInputBase-root': { height: 25, pr: 1 },
  '& .MuiInputLabel-root.Mui-focused': {
    fontSize: 16, // when focused
  },
  '& .MuiInputLabel-root': {
    fontSize: 12,
    '&:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 4px) scale(1)' },
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    fontSize: 12,
  },
};

const xsTextFieldSx = {
  '& .MuiInputBase-root': { height: 30, pr: 1 },
  '& .MuiInputLabel-root': {
    fontSize: 15,
    '&:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 4px) scale(1)' },
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    fontSize: 11.3,
  },
};

export interface FormDateFieldProps {
  mode?: DateEntryMode;
  label?: string;
  disablePast?: boolean;
  disableFuture?: boolean;
  disabled?: boolean;
  disableErrorText?: boolean;
  maxDateTime?: Date;
  minDateTime?: Date;
  size?: 'tiny' | 'xs';
  displayOnly?: boolean;
  'data-testid'?: string;
  'data-visual-test'?: 'transparent';
}

export function FormDateRangeField({
  mode,
  minDateTime,
  maxDateTime,
  size,
  displayOnly,
  disableErrorText,
  ...rest
}: Readonly<FormDateFieldProps>) {
  const field = useFieldContext<DateRange<Date>>();
  const error = useMemo(
    () => field.state.meta.errors[0]?.message as string | undefined,
    [field.state.meta.errors],
  );

  const onChange = useCallback(
    (date: DateRange<Date>) => {
      let [dateStart, dateEnd] = date;
      if (dateStart) {
        dateStart = getDate(dateStart, mode);
      }

      if (dateEnd) {
        dateEnd = getDate(dateEnd, mode);
      }

      field.handleChange([dateStart, dateEnd]);
    },
    [field, mode],
  );

  const commonProps = useMemo(() => {
    let sx: SxProps<Theme> | undefined;
    if (size === 'tiny') {
      sx = tinyTextFieldSx;
    } else if (size === 'xs') {
      sx = xsTextFieldSx;
    }

    return {
      label: rest.label ?? prettifySnakeCase(field.name.split('.').at(-1)),
      disablePast: rest.disablePast,
      disableFuture: rest.disableFuture,
      disabled: rest.disabled,
      value: field.state.value,
      onChange,
      slotProps: {
        textField: {
          error: Boolean(error),
          helperText: disableErrorText ? undefined : error,
          'data-testid': rest['data-testid'] ?? field.name.split('.').at(-1),
          size: 'small' as const,
          fullWidth: true,
          sx,
        },
      },
    };
  }, [
    rest['data-testid'],
    field.state.value,
    error,
    field.name,
    onChange,
    rest.label,
    rest.disablePast,
    rest.disableFuture,
    rest.disabled,
    size,
    disableErrorText,
  ]);

  return useMemo(
    () =>
      displayOnly ? (
        <InputKeyValue
          value={
            <FormatValue
              value={`${formatDate(commonProps.value[0])}-${formatDate(commonProps.value[1])}`}
            />
          }
          label={commonProps.label}
          sx={{ pt: 0, pb: 0 }}
        />
      ) : (
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          data-visual-test={rest['data-visual-test']}
        >
          <DateRangePicker
            {...commonProps}
            minDate={minDateTime}
            maxDate={maxDateTime}
            disableAutoMonthSwitching
          />
        </LocalizationProvider>
      ),
    [commonProps, minDateTime, maxDateTime, rest['data-visual-test'], displayOnly],
  );
}
